/**
 * 従業員/アカウント管理ページ (/employee/account-management)
 *
 * アカウント管理タブ。従業員ごとのログイン情報を表形式で表示する。
 * 列: 従業員コード / 従業員名 / 店舗 / 部門 / パスワード状態 / パスワード初期化 / 最終ログイン日時
 *
 * 画面右上の表示切替ボタンは、リスト(左)を選択固定・ツリー(右)を非活性にする。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { Button } from "../components/atoms";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableRow,
    CommonTableCell,
} from "../components/table";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "../components/atoms";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import {
    fetchEmployees,
    resetEmployeePassword,
    type EmployeeResponse,
} from "../api/employees";
import { fetchStores } from "../api/stores";
import type { RootState } from "../app/store";
import { ALL_SELECTION_ID } from "../app/store/storeSelectionSlice";
import { resolveStoreIdsForSelection } from "../shared/store-selection/resolveStoreIds";

const ListIcon = (p: React.SVGProps<SVGSVGElement>) => (
    <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
);

const GridIcon = (p: React.SVGProps<SVGSVGElement>) => (
    <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
);

/** アカウント管理タブ専用の ViewToggle（list 固定・tree 非活性） */
const LockedViewToggle = () => (
    <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
        <button
            type="button"
            disabled
            className={clsx("p-2 transition-colors", "bg-brand-500 text-white cursor-default")}
        >
            <ListIcon className="w-5 h-5" />
        </button>
        <button
            type="button"
            disabled
            className={clsx("p-2 transition-colors", "bg-white text-gray-300 cursor-not-allowed")}
        >
            <GridIcon className="w-5 h-5" />
        </button>
    </div>
);

const EMPTY = "—";
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

const displayValue = (val: string | number | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY : String(val);

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const PASSWORD_STATUS_LABEL: Record<NonNullable<EmployeeResponse["password_status"]>, string> = {
    normal: "正常",
    locked: "ロック",
    initial: "初期パスワード",
};

const passwordStatusToKey = (status: EmployeeResponse["password_status"]): string =>
    status ?? EMPTY_VALUE_KEY;

const passwordStatusLabel = (key: string): string => {
    if (key === EMPTY_VALUE_KEY) {return EMPTY_VALUE_LABEL;}
    if (key in PASSWORD_STATUS_LABEL) {
        return PASSWORD_STATUS_LABEL[key as keyof typeof PASSWORD_STATUS_LABEL];
    }
    return key;
};

const formatPasswordStatus = (status: EmployeeResponse["password_status"]): string =>
    status ? PASSWORD_STATUS_LABEL[status] : EMPTY;

type SortColumn = "code" | "name" | "store" | "dept" | "passwordStatus";

interface PasswordResetDialogProps {
    employee: EmployeeResponse;
    onSuccess: () => void;
    onCancel: () => void;
}

/** パスワード初期化の確認ダイアログ。実行中・エラー状態は内部で保持する */
const PasswordResetDialog = ({ employee, onSuccess, onCancel }: PasswordResetDialogProps) => {
    const [isResetting, setIsResetting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleConfirm = async () => {
        setIsResetting(true);
        setErrorMessage(null);
        try {
            await resetEmployeePassword(employee.id);
            onSuccess();
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : "パスワードの初期化に失敗しました");
        } finally {
            setIsResetting(false);
        }
    };

    const handleCancel = () => {
        if (isResetting) {return;}
        onCancel();
    };

    return (
        <Dialog onClose={isResetting ? undefined : handleCancel} className="w-[440px]">
            <DialogHeader
                title="パスワード初期化"
                onClose={isResetting ? undefined : handleCancel}
                className="border-b-0"
            />
            <DialogBody>
                <p className="text-sm text-gray-700">
                    <span className="font-medium">{employee.name}</span> さんのパスワードを初期化します。よろしいですか？
                </p>
                {errorMessage && (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                )}
            </DialogBody>
            <DialogFooter className="border-t-0">
                <Button
                    variant="outline"
                    size="md"
                    onClick={handleCancel}
                    disabled={isResetting}
                >
                    キャンセル
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleConfirm}
                    disabled={isResetting}
                >
                    {isResetting ? "初期化中..." : "初期化する"}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

const AccountManagementPage = () => {
    const menuTitle = useMenuTitle();
    const storeSelection = useSelector((state: RootState) => state.storeSelection);
    const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
    const [storeNameMap, setStoreNameMap] = useState<Record<number, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const selectedStoreId = storeSelection.store?.id;
    const isAllStores = storeSelection.store?.id === ALL_SELECTION_ID;
    const isAllDepartments = storeSelection.department?.id === ALL_SELECTION_ID;
    const selectedDepartmentId = storeSelection.department?.id;

    // 店舗一覧（store_id → 店舗名 マップ用）は一度だけ取得
    useEffect(() => {
        const loadStoreNames = async () => {
            try {
                const response = await fetchStores();
                const map: Record<number, string> = {};
                response.items.forEach((s) => {
                    map[s.id] = s.name;
                });
                setStoreNameMap(map);
            } catch {
                // 店舗名解決に失敗しても従業員一覧表示自体は継続
            }
        };
        loadStoreNames();
    }, []);

    // サイドメニューの店舗/部門選択に合わせて従業員一覧を取得
    const loadEmployees = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (isAllStores) {
                const storeIds = await resolveStoreIdsForSelection(storeSelection);
                const response = await fetchEmployees({
                    page_size: 100,
                    store_ids: storeIds.join(","),
                    department_id:
                        !isAllDepartments && selectedDepartmentId
                            ? selectedDepartmentId
                            : undefined,
                });
                setEmployees(response.items);
            } else if (selectedStoreId) {
                const response = await fetchEmployees({
                    page_size: 100,
                    store_id: selectedStoreId,
                    department_id:
                        !isAllDepartments && selectedDepartmentId
                            ? selectedDepartmentId
                            : undefined,
                });
                setEmployees(response.items);
            } else {
                const response = await fetchEmployees({ page_size: 100 });
                let items = response.items;
                const areaStoreIds = await resolveStoreIdsForSelection(storeSelection);
                if (areaStoreIds.length > 0) {
                    const storeIdSet = new Set(areaStoreIds);
                    items = items.filter((emp) => storeIdSet.has(emp.store_id));
                }
                setEmployees(items);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : "従業員データの取得に失敗しました");
        } finally {
            setIsLoading(false);
        }
    }, [
        storeSelection,
        selectedStoreId,
        isAllStores,
        isAllDepartments,
        selectedDepartmentId,
    ]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    // --- 列ごとの並び替え/絞り込み state ---
    const codeFilter = useColumnFilter(employees, (e) => toKey(e.code), labelOrEmpty);
    const nameFilter = useColumnFilter(employees, (e) => toKey(e.name), labelOrEmpty);
    const storeFilter = useColumnFilter(
        employees,
        (e) => storeNameMap[e.store_id] ?? EMPTY_VALUE_KEY,
        labelOrEmpty,
    );
    const deptFilter = useColumnFilter(
        employees,
        (e) => toKey(e.department_name),
        labelOrEmpty,
    );
    const passwordStatusFilter = useColumnFilter(
        employees,
        (e) => passwordStatusToKey(e.password_status),
        passwordStatusLabel,
    );

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // ヘッダー描画とポップアップ描画で共通利用する列定義
    const filterColumnList = useMemo(
        () => [
            { column: "code" as SortColumn, label: "従業員コード", widthClass: "min-w-[120px]", filter: codeFilter },
            { column: "name" as SortColumn, label: "従業員名", widthClass: "min-w-[160px]", filter: nameFilter },
            { column: "store" as SortColumn, label: "店舗", widthClass: "min-w-[140px]", filter: storeFilter },
            { column: "dept" as SortColumn, label: "部門", widthClass: "min-w-[140px]", filter: deptFilter },
            { column: "passwordStatus" as SortColumn, label: "パスワード状態", widthClass: "min-w-[140px]", filter: passwordStatusFilter },
        ],
        [codeFilter, nameFilter, storeFilter, deptFilter, passwordStatusFilter],
    );

    // 表示用にソート/絞り込みを適用
    const displayedEmployees = useMemo(() => {
        let list = employees;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((e) => set.has(toKey(e.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((e) => set.has(toKey(e.name)));
        }
        if (storeFilter.isFiltering) {
            const set = new Set(storeFilter.selected);
            list = list.filter((e) => set.has(storeNameMap[e.store_id] ?? EMPTY_VALUE_KEY));
        }
        if (deptFilter.isFiltering) {
            const set = new Set(deptFilter.selected);
            list = list.filter((e) => set.has(toKey(e.department_name)));
        }
        if (passwordStatusFilter.isFiltering) {
            const set = new Set(passwordStatusFilter.selected);
            list = list.filter((e) => set.has(passwordStatusToKey(e.password_status)));
        }

        if (activeSort) {
            const cmp = (a: EmployeeResponse, b: EmployeeResponse): number => {
                switch (activeSort.column) {
                    case "code":
                        return (a.code ?? "").localeCompare(b.code ?? "", "ja");
                    case "name":
                        return (a.name ?? "").localeCompare(b.name ?? "", "ja");
                    case "store":
                        return (storeNameMap[a.store_id] ?? "").localeCompare(
                            storeNameMap[b.store_id] ?? "",
                            "ja",
                        );
                    case "dept":
                        return (a.department_name ?? "").localeCompare(
                            b.department_name ?? "",
                            "ja",
                        );
                    case "passwordStatus":
                        return passwordStatusLabel(passwordStatusToKey(a.password_status))
                            .localeCompare(
                                passwordStatusLabel(passwordStatusToKey(b.password_status)),
                                "ja",
                            );
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        employees,
        storeNameMap,
        codeFilter.isFiltering, codeFilter.selected,
        nameFilter.isFiltering, nameFilter.selected,
        storeFilter.isFiltering, storeFilter.selected,
        deptFilter.isFiltering, deptFilter.selected,
        passwordStatusFilter.isFiltering, passwordStatusFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const target = filterColumnList.find((c) => c.column === which);
            if (!target) {return;}
            target.filter.anchorRef.current = anchor;
            const willOpen = !target.filter.open;
            target.filter.setOpen(willOpen);
            if (willOpen) {
                filterColumnList.forEach((c) => {
                    if (c.column !== which) {c.filter.setOpen(false);}
                });
            }
        },
        [filterColumnList],
    );

    // パスワード初期化の確認ダイアログ
    const [resetTarget, setResetTarget] = useState<EmployeeResponse | null>(null);

    const handleResetClick = (employee: EmployeeResponse) => {
        setResetTarget(employee);
    };

    const handleResetSuccess = () => {
        setResetTarget(null);
        loadEmployees();
    };

    const handleResetCancel = () => {
        setResetTarget(null);
    };

    return (
        <>
            <div className="flex flex-1 flex-col overflow-hidden">
                <PageHeader title={menuTitle} rightContent={<LockedViewToggle />} />
                <TabBar />

                <div className="flex-1 overflow-auto px-6 pb-6">
                    {error && (
                        <div className="mb-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                    <CommonTable>
                        <thead className="sticky top-0 z-20">
                            <tr>
                                {filterColumnList.map((c) => (
                                    <CommonTableHeaderCell
                                        key={c.column}
                                        sortable
                                        sortActive={c.filter.open}
                                        sortDirection={sortFor(c.column)}
                                        filterCount={c.filter.filterCount}
                                        onSort={(anchor) => openOnly(c.column, anchor)}
                                        className={c.widthClass}
                                    >
                                        {c.label}
                                    </CommonTableHeaderCell>
                                ))}
                                <CommonTableHeaderCell className="min-w-[140px]">パスワード初期化</CommonTableHeaderCell>
                                <CommonTableHeaderCell className="min-w-[180px]">最終ログイン日時</CommonTableHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={7} className="text-gray-400">
                                        読み込み中…
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : displayedEmployees.length === 0 ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={7} className="text-gray-400">
                                        対象の従業員がいません
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : (
                                displayedEmployees.map((emp) => (
                                    <CommonTableRow key={emp.id}>
                                        <CommonTableCell>{displayValue(emp.code)}</CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.name)}</CommonTableCell>
                                        <CommonTableCell>
                                            {displayValue(storeNameMap[emp.store_id])}
                                        </CommonTableCell>
                                        <CommonTableCell>{displayValue(emp.department_name)}</CommonTableCell>
                                        <CommonTableCell>
                                            {formatPasswordStatus(emp.password_status)}
                                        </CommonTableCell>
                                        <CommonTableCell>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleResetClick(emp)}
                                            >
                                                初期化
                                            </Button>
                                        </CommonTableCell>
                                        <CommonTableCell>
                                            <span className="text-gray-400">{EMPTY}</span>
                                        </CommonTableCell>
                                    </CommonTableRow>
                                ))
                            )}
                        </tbody>
                    </CommonTable>
                </div>
            </div>

            {/* 列ごとの並び替え＋絞り込みポップアップ */}
            {filterColumnList.map((c) => (
                <ColumnFilterPopup
                    key={c.column}
                    isOpen={c.filter.open}
                    onClose={() => c.filter.setOpen(false)}
                    anchorRef={c.filter.anchorRef}
                    sortDirection={sortFor(c.column)}
                    onSortChange={handleSortChange(c.column)}
                    options={c.filter.options}
                    selectedValues={c.filter.selected}
                    onSelectedValuesChange={c.filter.setSelected}
                />
            ))}

            {/* パスワード初期化 確認ダイアログ */}
            {resetTarget && (
                <PasswordResetDialog
                    employee={resetTarget}
                    onSuccess={handleResetSuccess}
                    onCancel={handleResetCancel}
                />
            )}
        </>
    );
};

export default AccountManagementPage;
